import { Component, ElementRef, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { distinctUntilChanged, filter, Observable, of, startWith, Subject, take } from "rxjs";
import { LoaderService } from "../../../../../../shared/src/lib/services/loader.service";
import { MarketService } from "../../../../../../shared/src/lib/services/market.service";
import { LiveUpdateService } from "../../../../../../shared/src/lib/services/live-update.service";
import { PopularEventsService } from "../../../../../../shared/src/lib/services/popular-events.service";
import { MarketOpsService } from "./market/market-ops.service";
import { BetPlacementService } from "../../../../../../shared/src/lib/services/bet-placement.service";
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from "@angular/router";
import { map, switchMap, takeUntil } from "rxjs/operators";
import { AsyncPipe, NgClass } from "@angular/common";
import { MarketComponent } from "./market/market.component";
import { MatAccordion } from "@angular/material/expansion";
import { EMarketsLoaderComponent } from "./e-markets-loader/e-markets-loader.component";
import { BookmakerComponent } from "./bookmaker/bookmaker.component";
import { FancyMarketComponent } from "./fancy-market/fancy-market.component";
import { AlertComponent } from "../../../components/alert/alert.component";
import { layLessSports } from "../../../../assets/constants/market";
import { Market } from "../../../../../../shared/src/lib/types/bet.type";
import { MatIconModule } from "@angular/material/icon";
import { TranslateModule } from "@ngx-translate/core";
import { WhiteLabelConfigService } from "../../../../../../shared/src/lib/services/white-label-config.service";
import { EventHeaderComponent } from "./event-header/event-header.component";

const defaultTabs = ["All Markets", "Main Market", "Bookmaker", "Fancy", "Line Market"];

@Component({
  selector: "app-market-layout",
  imports: [
    NgClass,
    MarketComponent,
    MatAccordion,
    AsyncPipe,
    EMarketsLoaderComponent,
    BookmakerComponent,
    FancyMarketComponent,
    AlertComponent,
    MatIconModule,
    RouterOutlet,
    RouterLink,
    TranslateModule,
    EventHeaderComponent,
  ],
  templateUrl: "./market-layout.component.html",
  styleUrl: "./market-layout.component.scss",
})
export class MarketLayoutComponent implements OnInit, OnDestroy {
  loader = inject(LoaderService);
  marketService = inject(MarketService);
  liveUpdateService = inject(LiveUpdateService);
  popularEventsService = inject(PopularEventsService);
  marketOps = inject(MarketOpsService);
  wlConfigService = inject(WhiteLabelConfigService);
  betPlacementService = inject(BetPlacementService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  elRef = inject(ElementRef);

  eventItem: any = null;
  eventBackup: any = null;
  isInPlay = signal<boolean>(false);
  loading$!: Observable<boolean>;
  parents: any[] = [];
  marketTabs = defaultTabs;
  racingTabLabels: Record<string, string> = {};
  activeTab: string = "All Markets";
  isLoadingStatus = signal(false);
  scrollTimeout: any;
  isActive = false;
  previousUrl: string | null = null;
  showBmAndFancy: boolean = true;
  readonly destroy$ = new Subject<void>();
  marketId: string = "";

  get isRacing() {
    return layLessSports.includes(this.eventBackup?.eventType?.id);
  }

  get isSoccer() {
    return this.eventBackup?.eventType?.id === "1";
  }

  get isCricket() {
    return this.eventBackup?.eventType?.id === "4";
  }

  ngOnInit() {
    this.loading$ = this.marketOps.isMarketLoading();
    this.showBmAndFancy = this.wlConfigService.showFeature("SB-BMAndFancy");
    const showMatchOdds0Percent = this.wlConfigService.showFeature("matchOdds0%Commission");
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.fetchEventMarkets(params["eventId"]);
      if (this.showBmAndFancy || showMatchOdds0Percent) this.getBmAndFancyMarkets(params["eventId"]);
    });
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(true),
        map(() => {
          let child = this.route;
          while (child.firstChild) {
            child = child.firstChild;
          }
          return child;
        }),
        switchMap((child) => child.paramMap),
        map((params) => ({
          eventId: params.get("eventId"),
          marketId: params.get("marketId"),
        })),
        distinctUntilChanged((prev, curr) => prev.eventId === curr.eventId && prev.marketId === curr.marketId),
      )
      .subscribe(({ eventId, marketId }) => {
        this.marketId = marketId ?? "";
        if ((eventId || marketId) && this.isRacing) this.fetchEventMarkets(eventId ?? "", marketId);
      });

    this.getNavMarket();
    window.addEventListener("keydown", this.handleKeydown.bind(this));

    this.marketOps.allMarkets$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        if (data) {
          const marketTopics = data?.map((market: any) => market?.topic ?? `bf_${market?.marketId}`);
          this.marketOps.setTopics(marketTopics);
        }
      },
    });

    this.manageTopicSubscription();
    this.trackMarketView();
  }

  trackMarketView() {
    if (this.marketOps.singleMarketSelected) this.activeTab = "";
  }

  manageTopicSubscription() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;

        const hadMarket = this.previousUrl?.includes("/market/");
        const hasMarket = url.includes("/market/");

        if (hadMarket && !hasMarket) {
          // console.log("Market segment was removed.");
          this.setActiveTab(this.isRacing ? this.activeTab : "All Markets");
          const allMarkets = this.marketOps.allMarkets$.getValue();
          const marketTopics = allMarkets?.map((market: any) => market?.topic ?? `bf_${market?.marketId}`);
          this.marketOps.setTopics(marketTopics);
        } else if (!hadMarket && hasMarket) {
          // console.log("Market segment was added.");
          this.activeTab = this.marketId;
        }

        this.previousUrl = url;
      });
  }

  getNavMarket() {
    this.marketOps.navMarket$.pipe(takeUntil(this.destroy$)).subscribe((selectedMarket) => {
      if (selectedMarket) {
        if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.scrollToMarket(selectedMarket);
        }, 0);
      }
    });
  }

  scrollToMarket(marketId: string) {
    if (this.isRacing) this.scrollToTab(marketId);
    const cleanId = marketId?.replace(".", "_");
    if (!cleanId) return;

    const targetElement = this.elRef.nativeElement.querySelector(`#m_${cleanId}`);

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "instant", block: "center" });
      targetElement.focus({ preventScroll: true });
    } else {
      if (this.isRacing) {
        this.handleRaceScrolling(marketId);
      } else if (this.activeTab !== "All Markets") {
        this.setActiveTab("All Markets");
        this.marketOps.navMarket$.next(marketId);
      }
    }
  }

  handleRaceScrolling(marketId: string) {
    const marketPool = this.marketOps.marketPool$.getValue();
    const targetMarket = marketPool.find((m: any) => m?.marketId === marketId);
    if (targetMarket) this.setActiveTab(targetMarket?.compareKey);
  }

  scrollToTab(marketId: string) {
    const marketPool = this.marketOps.marketPool$.getValue();
    const targetMarket = marketPool.find((m: any) => m?.marketId === marketId);
    const targetTab = targetMarket?.compareKey?.replace(".", "_");
    const targetElement = this.elRef.nativeElement.querySelector(`#m_${targetTab}`);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", inline: "start" });
      targetElement.focus({ preventScroll: true });
    }
  }

  scrollNav(amount: number) {
    if (!this.marketTabs?.length) return;
    const activeIndex = this.marketTabs.indexOf(this.activeTab);
    if (activeIndex === -1) return;
    const lastIndex = this.marketTabs?.length - 1;

    const left = activeIndex - 1;
    const right = activeIndex + 1;
    const isLeft = amount < 0;

    const nextIndex = isLeft ? (activeIndex > 0 ? left : lastIndex) : activeIndex < lastIndex ? right : 0;

    this.setActiveTab(this.marketTabs?.[nextIndex]);

    const navContainer = document.querySelector(".market-nav") as HTMLElement;

    if (navContainer) {
      if (isLeft && nextIndex === lastIndex) {
        // Loop to end → scroll to far right
        navContainer.scrollTo({ left: navContainer.scrollWidth, behavior: "smooth" });
      } else if (!isLeft && nextIndex === 0) {
        // Loop to start → scroll to far left
        navContainer.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        // Regular scroll
        navContainer.scrollBy({ left: amount, behavior: "smooth" });
      }
    }
  }

  setActiveTab(nav: string) {
    this.activeTab = nav;
    this.filterMarketsByTab(nav);
  }

  handleTabSelection(nav: string) {
    if (!this.activeTab) this.activeTab = nav;
    this.setActiveTab(nav);
  }

  filterMarketsByTab(currentTab: string) {
    const marketPool = this.marketOps.marketPool$.getValue();
    let filteredItems = [];

    if (currentTab === "All Markets") {
      filteredItems = marketPool?.filter((market: any) => !market?.isAsianHandicap && !market?.showUnderFancy);
    } else if (currentTab === "Main Market") {
      filteredItems = marketPool.filter((market: any) => {
        return market?.isMain || market?.isMOddsBookmaker || market?.showUnderMain;
      });
    } else if (["Bookmaker", "Goal Markets"].includes(currentTab)) {
      filteredItems = marketPool.filter((market: any) => {
        return market?.isBookmaker || market?.isGoal;
      });
    } else if (["Fancy", "Others"].includes(currentTab)) {
      filteredItems = marketPool.filter((market: any) => {
        return (market?.isFancy || market?.showUnderFancy) && market?.runners?.length > 0;
      });
    } else if (["Line Market", "Asian Handicap"].includes(currentTab)) {
      filteredItems = marketPool.filter((market: any) => {
        return market?.isLine || market?.showUnderLine;
      });
    } else {
      filteredItems = marketPool.filter((market: any) => {
        return market?.compareKey === currentTab;
      });
    }

    this.marketOps.allMarkets$.next(filteredItems);
  }

  getBmAndFancyMarkets(eventId?: string) {
    if (!eventId) return;

    this.marketService
      .fetchSbBmAndFancyMarkets({ eventId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.data) {
            const { bookmaker, fancy } = res?.data || {};
            const bookmakerMarkets =
              bookmaker?.map((market: any) => ({
                ...market,
                isBookmaker: market?.providerId !== "0",
                isMOddsBookmaker: market?.providerId === "0",
              })) ?? [];
            const fancyMarkets =
              fancy?.map((market: any) => ({
                ...market,
                isFancy: true,
              })) ?? [];
            this.marketOps.setMarketPool([...bookmakerMarkets, ...fancyMarkets]);
          }
        },
        error: (err) => {
          console.error("Error fetching bookmaker markets:", err);
        },
      });
  }

  fetchEventMarkets(eventId: string, marketId?: string | null) {
    this.eventItem = null;
    this.isLoadingStatus.set(true);

    // marketId is provided → SKIP getEventMarkets and go straight to racing markets
    if (marketId) {
      this.marketService
        .getRacingMarkets({ id: marketId })
        .pipe(takeUntil(this.destroy$))
        .pipe(map((response: any) => response?.data))
        .pipe(
          map((data: any) => {
            if (!data) return data;
            const firstMarket = data?.markets?.find((market: any) => market?.marketId === marketId);
            const openDate = firstMarket?.marketStartTime;

            const newEvent = { ...data?.event, openDate };
            const newMarkets = data?.markets?.map((market: any) => ({
              ...market,
              event: { ...market?.event, openDate: market?.marketStartTime },
            }));
            return { ...data, event: newEvent, markets: newMarkets };
          }),
        )
        .subscribe({
          next: (data: any) => {
            if (!data) {
              this.isLoadingStatus.set(false);
              return;
            }

            // After racing markets, continue and load status
            if (data?.markets?.length > 0) {
              const marketTopics = data.markets.map((m: any) => `bf_${m.marketId}`);
              const params = { market_ids: marketTopics.join(",") };
              this.getMarketStatus(params, data);
            } else {
              this.isLoadingStatus.set(false);
            }
          },
          error: () => this.isLoadingStatus.set(false),
        });

      return; // stop execution here
    }

    // marketId is NOT provided → FALL BACK to original logic
    this.marketService
      .getEventMarkets({ id: eventId })
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: any) => {
          const data = response?.data;
          if (!data) {
            this.isLoadingStatus.set(false);
            return of(null);
          }

          const isRacing = layLessSports.includes(data?.eventType?.id);
          const hasMarkets = data?.markets?.length;

          // if racing → call a different API before continuing
          if (isRacing && hasMarkets) {
            const firstMarket = () => {
              const defaultFirst = data?.markets?.[0];
              if (this.marketId) {
                const matchingMarket = data?.markets?.find((market: any) => market?.marketId === this.marketId);
                return matchingMarket ?? defaultFirst;
              }
              return defaultFirst;
            };
            const openDate = firstMarket()?.marketStartTime;

            return this.marketService.getRacingMarkets({ id: firstMarket()?.marketId }).pipe(
              map((response: any) => {
                const data = response?.data;
                const newEvent = { ...data?.event, openDate };
                const newMarkets = data?.markets?.map((market: any) => ({
                  ...market,
                  event: { ...market?.event, openDate: market?.marketStartTime },
                }));
                return { ...data, event: newEvent, markets: newMarkets };
              }),
            );
          }

          return of(data);
        }),
      )
      .subscribe({
        next: (data: any) => {
          if (!data) return;

          const isArray = Array.isArray(data);
          const isEmptyObject = typeof data === "object" && !isArray && Object.keys(data)?.length === 0;

          if (isArray || isEmptyObject) {
            this.isLoadingStatus.set(false);
            return;
          }

          if (data?.event) this.popularEventsService.add(data?.event);

          if (data?.markets?.length > 0) {
            const marketTopics = data.markets.map((m: any) => `bf_${m.marketId}`);
            const params = { market_ids: marketTopics.join(",") };
            this.getMarketStatus(params, data);
          } else {
            this.isLoadingStatus.set(false);
          }
        },
        error: () => this.isLoadingStatus.set(false),
      });
  }

  getMarketStatus(params: any, event: any) {
    const qParams = { ...params, statuses: "2,3", return_snapshot: "Y" };

    this.marketService
      .fetchMarketStatus(qParams)
      .pipe(
        switchMap((response: any) => {
          const { snapshot: snapshots = {}, statuses = {} } = response || {};
          return this.marketOps.navBasedSortPriority$.pipe(
            take(1),
            map((priorities) => ({ snapshots, statuses, priorities })),
          );
        }),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ snapshots, statuses, priorities }) => {
          if (event?.markets?.length > 0 && priorities) {
            const isRacing = layLessSports.includes(event?.eventType?.id);
            const eventMarkets = isRacing ? this.racingMarkets(event?.markets) : event?.markets;
            const openedMarkets = statuses["2"] || [];
            const suspendedMarkets = statuses["3"] || [];

            const _markets = eventMarkets
              .filter((market: any) => {
                const _marketId = `bf_${market?.marketId}`;
                return openedMarkets.includes(_marketId) || suspendedMarkets.includes(_marketId);
              })
              .map((market: any) => {
                const _marketId = `bf_${market?.marketId}`;
                const sortPriority = priorities.get(market?.marketId);
                const isSoccer = market?.eventType?.id === "1";
                const includesGoal = market?.marketName?.includes("Goals");
                const isARacingWinMarket = isRacing && market?.description?.["marketType"] === "WIN";
                const isMain = market?.marketName === "Match Odds" || isARacingWinMarket || sortPriority === 0;
                const isAsianHandicap = market?.description?.bettingType?.includes("ASIAN_HANDICAP");
                const isGoal = isSoccer && includesGoal && !isAsianHandicap;
                const showUnderFancy = isSoccer && !isMain && !includesGoal && !isAsianHandicap;
                const showUnderLine = isSoccer && isAsianHandicap;
                const priority = isGoal ? market?.marketName : sortPriority;

                const baseMarket = {
                  ...market,
                  mc: snapshots[_marketId],
                  eventId: market?.event?.id,
                  eventTypeId: market?.eventType?.id,
                  priority,
                  isMain,
                };

                if (isRacing) return baseMarket;

                return {
                  ...baseMarket,
                  isMain,
                  showUnderMain: !["1", "4"].includes(market?.eventType?.id),
                  isLine: market?.description?.bettingType === "LINE",
                  isGoal,
                  isAsianHandicap,
                  showUnderFancy,
                  showUnderLine,
                };
              })
              .filter((market: any) => {
                const showLineMarket = this.wlConfigService.showFeature("lineMarket");
                const isCricket = market?.eventType?.id === "4";
                const isLine = market?.description?.bettingType?.includes("LINE");
                if (!showLineMarket && isLine && isCricket) return;
                return market;
              });

            const _event = {
              ...event,
              markets: _markets,
            };

            this.setBetfairMarkets(_event);
          }
        },
        error: (error) => {
          console.error("Failed to fetch market statuses:", error);
        },
      });
  }

  setBetfairMarkets(eventItem: any) {
    const filteredMarkets = eventItem?.markets;
    this.eventItem = filteredMarkets.length > 0 ? eventItem : null;
    this.eventBackup = this.eventItem;
    this.betPlacementService.currentPlacementConcernsMe({ ...this.eventItem, eventId: this.eventItem?.event?.id }, "eventId");
    const initParams = this.isRacing ? { initWith: this.marketId || this.marketTabs?.[0], initCompareKey: "compareKey" } : undefined;
    if (!initParams) this.setDefaultTabs(defaultTabs);
    this.marketOps.setMarketPool(filteredMarkets, initParams);
    this.isLoadingStatus.set(false);
  }

  racingMarkets(markets: Market[]) {
    this.marketTabs = markets?.map((market) => market?.marketId);
    const marketsWithCompareKey = markets?.map((market) => {
      this.racingTabLabels[market?.marketId] = market?.marketName;
      return { ...market, compareKey: market?.marketId };
    });

    const routeMarketExists = markets?.some((market) => market?.marketId === this.marketId);

    if (routeMarketExists) {
      this.activeTab = this.marketId;
    } else {
      this.activeTab = this.marketTabs?.[0];
      this.marketId = "";
    }

    return marketsWithCompareKey ?? markets;
  }

  handleKeydown(event: KeyboardEvent) {
    if (!this.isActive) return;

    if (event.key === "ArrowLeft") {
      this.scrollNav(-200);
    } else if (event.key === "ArrowRight") {
      this.scrollNav(200);
    }
  }

  setDefaultTabs(tabs: string[]) {
    const showLineMarketTab = this.wlConfigService.showFeature("lineMarket");
    this.marketTabs = tabs
      .map((tab) => {
        if (!this.isCricket && tab === "Fancy") return "Others";
        if (this.isSoccer && tab === "Bookmaker") return "Goal Markets";
        if (this.isSoccer && tab === "Line Market") return "Asian Handicap";
        return tab;
      })
      .filter((tab) => {
        if (tab === "Line Market" && !this.isSoccer) return showLineMarketTab;
        if (["Bookmaker", "Fancy"].includes(tab) && !this.isSoccer) return this.showBmAndFancy;
        return true;
      });
    if (!this.marketOps.singleMarketSelected) this.activeTab = "All Markets";
  }

  ngOnDestroy(): void {
    this.marketOps.setTopics([]);
    this.marketOps.marketPool$.next([]);
    this.marketOps.allMarkets$.next([]);
    window.removeEventListener("keydown", this.handleKeydown.bind(this));
    if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
